/**
 * Проверка листов перед сохранением: тот же инвариант, что и в work-calc
 * (`KT\Applier\Service\TariffSheetGrid`).
 *
 * Полосы листа обязаны быть разбиением `[0, ∞)` — без перекрытий и без дырок, — и у каждой
 * пары полос обязана быть цена. Перекрытие внутри листа означает, что одна из двух цен
 * листа мертва; дырка и пустая ячейка — что в закрытие уйдёт 0.
 *
 * Почему проверка живёт и здесь тоже: шлюз `/services/rates` тело ошибки не прокидывает —
 * на 400 он отдаёт `{"error": "400"}`, и текст, который work-calc написал пользователю, до
 * экрана не доходит. Поэтому сообщение показывает фронт (тексты совпадают дословно), а
 * отказ work-calc остаётся страховкой на случай запроса мимо экрана.
 *
 * Сведение границ листов к общей сетке фронт НЕ делает: это работа сохранения, и повторять
 * её здесь незачем — payload и так уходит с явными границами.
 */

import { RateTableModel, SheetModel } from '@/components/RatesPage/types';

const EPS = 0.000001;
const INF = Number.POSITIVE_INFINITY;

const PROPERTY_TITLES: Array<{ key: 'performance' | 'quality'; title: string }> = [
  { key: 'performance', title: 'Производительность' },
  { key: 'quality', title: 'Качество' },
];

interface Bounds {
  lo: number;
  hi: number;
}

function bounds(band: { from: number | null; to: number | null } | null | undefined): Bounds {
  const lo = band?.from == null || (band.from as unknown) === '' ? 0 : Number(band.from);
  const rawTo = band?.to;
  const hi = rawTo == null || (rawTo as unknown) === '' ? INF : Number(rawTo);

  return { lo, hi: hi === -1 ? INF : hi };
}

function bandKey(band: Bounds): string {
  return `${band.lo}_${band.hi === INF ? 'inf' : band.hi}`;
}

function percent(value: number): string {
  return String(Math.round(value * 10000) / 10000);
}

function bandTitle(band: Bounds): string {
  return band.hi === INF
    ? `от ${percent(band.lo)} % и выше`
    : `от ${percent(band.lo)} до ${percent(band.hi)} %`;
}

function bandsOf(cells: Array<RateTableModel>, property: 'performance' | 'quality'): Array<Bounds> {
  const seen: Record<string, Bounds> = {};
  cells.forEach((cell) => {
    const band = bounds(cell[property]);
    seen[bandKey(band)] = band;
  });

  return Object.keys(seen).map((key) => seen[key]);
}

/**
 * Полосы листа обязаны идти стык в стык от 0 до открытого верха: любое расхождение —
 * перекрытие или дырка.
 */
function partitionProblems(bands: Array<Bounds>): Array<string> {
  if (bands.length === 0) {
    return [];
  }

  const sorted = bands.slice().sort((a, b) => (a.lo - b.lo) || (a.hi - b.hi));
  const problems: Array<string> = [];

  if (sorted[0].lo > EPS) {
    problems.push(
      `процент ${bandTitle({ lo: 0, hi: sorted[0].lo })} ни в одну полосу не попадает`
      + ` — в закрытие уйдёт 0. Опустите нижнюю границу полосы ${bandTitle(sorted[0])} до 0.`,
    );
  }

  for (let i = 0; i < sorted.length - 1; i += 1) {
    const current = sorted[i];
    const next = sorted[i + 1];

    if (current.hi === INF) {
      problems.push(
        `полосы ${bandTitle(current)} и ${bandTitle(next)} перекрываются.`
        + ' Уберите лишнюю полосу или поправьте её границы.',
      );
    } else if (next.lo > current.hi + EPS) {
      problems.push(
        `процент ${bandTitle({ lo: current.hi, hi: next.lo })} ни в одну полосу не попадает`
        + ` — в закрытие уйдёт 0. Сомкните границы полос ${bandTitle(current)}`
        + ` и ${bandTitle(next)}.`,
      );
    } else if (next.lo < current.hi - EPS) {
      problems.push(
        `полосы ${bandTitle(current)} и ${bandTitle(next)} перекрываются.`
        + ' Уберите лишнюю полосу или поправьте её границы.',
      );
    }
  }

  const top = sorted[sorted.length - 1];
  if (top.hi !== INF) {
    problems.push(
      `процент ${bandTitle({ lo: top.hi, hi: INF })} ни в одну полосу не попадает`
      + ` — в закрытие уйдёт 0. Снимите верхнюю границу у полосы ${bandTitle(top)}.`,
    );
  }

  return problems;
}

/** Пары полос без цены: полосы заведены, а закрытие на таком проценте получит 0. */
function missingPairs(cells: Array<RateTableModel>): Array<string> {
  const performance = bandsOf(cells, 'performance');
  const quality = bandsOf(cells, 'quality');
  if (performance.length === 0 || quality.length === 0) {
    return [];
  }

  const filled: Record<string, boolean> = {};
  cells.forEach((cell) => {
    filled[`${bandKey(bounds(cell.performance))}|${bandKey(bounds(cell.quality))}`] = true;
  });

  const problems: Array<string> = [];
  performance.forEach((perf) => {
    quality.forEach((qual) => {
      if (filled[`${bandKey(perf)}|${bandKey(qual)}`]) {
        return;
      }
      problems.push(
        `у пары полос Производительность ${bandTitle(perf)} и Качество ${bandTitle(qual)}`
        + ' нет цены — в закрытие уйдёт 0. Заполните все ячейки листа.',
      );
    });
  });

  return problems;
}

/**
 * Номер листа совпадает с подписью «Лист N из M» на экране. Единственный лист номера не
 * получает — уточнять нечего, и «Лист, Производительность: …» читалось бы как перечисление.
 */
function sheetTitle(index: number, total: number): string {
  return total > 1 ? `Лист ${index + 1} из ${total}` : '';
}

/**
 * Проблемы всех листов вида работ. Пустой массив = сохранять можно.
 */
export function sheetGridProblems(sheets: Array<SheetModel> | null | undefined): Array<string> {
  const list = (sheets ?? []).filter((sheet) => (sheet?.cells?.length ?? 0) > 0);
  const problems: Array<string> = [];

  list.forEach((sheet, index) => {
    const cells = (sheet.cells ?? []) as Array<RateTableModel>;
    const title = sheetTitle(index, list.length);

    PROPERTY_TITLES.forEach(({ key, title: property }) => {
      partitionProblems(bandsOf(cells, key)).forEach((problem) => {
        problems.push(title === '' ? `${property}: ${problem}` : `${title}, ${property}: ${problem}`);
      });
    });

    missingPairs(cells).forEach((problem) => {
      problems.push(`${title === '' ? 'Лист' : title}: ${problem}`);
    });
  });

  return problems;
}

/** Первые проблемы одной строкой — то же сообщение, что вернул бы work-calc. */
export function sheetGridMessage(
  sheets: Array<SheetModel> | null | undefined,
  maxReported = 3,
): string | null {
  const problems = sheetGridProblems(sheets);
  if (problems.length === 0) {
    return null;
  }

  const shown = problems.slice(0, maxReported);
  const rest = problems.length - shown.length;
  const tail = rest > 0 ? ` И ещё ${rest} такая же проблема в этом виде работ.` : '';

  return `Тариф не сохранён. ${shown.join(' ')}${tail}`;
}
